// src/pages/ConnectionsPage.js
import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Preferences } from "@capacitor/preferences";
import { api } from "api";
import { CheckCircle } from "phosphor-react";
import BackButton from "../components/BackButton";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

/* -------------------- small helpers (copied from modal) -------------------- */
const toAvatar = (u) =>
  u?.profile_pic_url || u?.avatar_url || u?.photo_url || u?.image_url || DEFAULT_PROFILE_IMAGE;

/** Pull a user object out of many possible server shapes */
function pickUser(row) {
  const u =
    row?.user ||
    row?.member ||
    row?.following ||
    row?.follower ||
    row?.addressee ||
    row?.receiver ||
    row?.requester ||
    row?.sender ||
    row; // fall back to row itself

  const name =
    u?.display_name ||
    u?.name ||
    [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
    u?.username ||
    (u?.id ? `User #${u.id}` : "User");

  return {
    id: u?.id,
    name,
    username: u?.username,
    profile_pic_url: u?.profile_pic_url || u?.avatar_url || u?.photo_url || u?.image_url || null,
    avatar_url: u?.avatar_url || null,
  };
}

function normalizeList(data) {
  const arr =
    Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.users)
      ? data.users
      : [];
  return arr.map(pickUser).filter((u) => u?.id != null);
}

/* -------------------- iOS-safe auth helpers -------------------- */
const getToken = async () => {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  const t =
    (typeof window !== "undefined" && (localStorage.getItem("token") || sessionStorage.getItem("token"))) ||
    null;
  return t;
};

const getAuthHeaders = async () => {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getMeId = async () => {
  try {
    const r = await Preferences.get({ key: "user_id" });
    if (r?.value) return Number(r.value);
  } catch {}
  if (typeof window !== "undefined") {
    const v = localStorage.getItem("user_id");
    if (v) return Number(v);
  }
  try {
    const headers = await getAuthHeaders();
    if (!headers.Authorization) return null;
    const { data } = await api.get("/users/me", { headers });
    if (data?.id) {
      try { await Preferences.set({ key: "user_id", value: String(data.id) }); } catch {}
      if (typeof window !== "undefined") localStorage.setItem("user_id", String(data.id));
      return Number(data.id);
    }
  } catch {}
  return null;
};

/* -------------------- Page -------------------- */
export default function ConnectionsPage({ meId: meIdProp = null, initialTab = "followers" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(initialTab); // "followers" | "following" | "requests"
  // Read query param on mount/URL change
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const q = sp.get("tab");
    if (q === "followers" || q === "following" || q === "requests") {
      setTab(q);
    }
  }, [location.search]);

  const [meId, setMeId] = useState(meIdProp);

  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);

  const [recv, setRecv] = useState([]); // requests received
  const [sent, setSent] = useState([]); // requests sent

  const [busy, setBusy] = useState({}); // per-user/request action spinner

  const [headers, setHeaders] = useState({});
  const hasAuth = "Authorization" in headers;

  useEffect(() => {
    (async () => {
      const h = await getAuthHeaders();
      setHeaders(h);
      if (!meIdProp) {
        const id = await getMeId();
        setMeId(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------ fetchers (same endpoints as modal) ------------ */
  const fetchFollowers = async () => {
    if (!meId) return setFollowers([]);
    try {
      const { data } = await api.get(`/connections/followers/${meId}`, { headers });
      setFollowers(normalizeList(data));
    } catch {
      setFollowers([]);
    }
  };

  const fetchFollowing = async () => {
    if (!meId) return setFollowing([]);
    try {
      const { data } = await api.get(`/connections/following/${meId}`, { headers });
      setFollowing(normalizeList(data));
    } catch {
      setFollowing([]);
    }
  };

  const fetchRequests = async () => {
    if (!hasAuth) {
      setRecv([]);
      setSent([]);
      return;
    }
    try {
      const rcv = await api.get(`/connections/requests`, { headers });
      setRecv(Array.isArray(rcv.data) ? rcv.data : []);
    } catch {
      setRecv([]);
    }
    try {
      const s =
        (await api
          .get(`/connections/requests/sent`, { headers })
          .catch(() => api.get(`/connections/sent`, { headers }))).data || [];
      setSent(Array.isArray(s) ? s : []);
    } catch {
      setSent([]);
    }
  };

  // initial + on tab changes
  useEffect(() => {
    if (!meId) return;
    if (tab === "followers") fetchFollowers();
    if (tab === "following") fetchFollowing();
    if (tab === "requests") fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, meId, headers.Authorization]);

  // Always show the top when switching tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  /* ------------ actions (parity with modal) ------------ */
  const amFollowing = (id) => following.some((u) => String(u.id) === String(id));

  const removeFollowing = async (otherId) => {
    if (busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      await api
        .delete(`/connections/following/${otherId}`, { headers })
        .catch(() => api.delete(`/connections/${otherId}`, { headers }))
        .catch(() => api.delete(`/connections`, { headers, data: { other_id: otherId } }));
      setFollowing((prev) => prev.filter((u) => String(u.id) !== String(otherId)));
    } catch (e) {
      console.error("removeFollowing failed", e?.response?.data || e.message);
      alert("Couldn’t remove. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const removeFollower = async (otherId) => {
    if (busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      await api
        .delete(`/connections/followers/${otherId}`, { headers })
        .catch(() => api.delete(`/connections/${otherId}`, { headers }))
        .catch(() => api.delete(`/connections`, { headers, data: { other_id: otherId } }));
      setFollowers((prev) => prev.filter((u) => String(u.id) !== String(otherId)));
    } catch (e) {
      console.error("removeFollower failed", e?.response?.data || e.message);
      alert("Couldn’t remove follower. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const followBack = async (otherId) => {
    if (busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      const { data } = await api.post(`/connections/request`, { addressee_id: otherId }, { headers });
      if (data?.status === "accepted") await fetchFollowing();
    } catch (e) {
      console.error("followBack failed", e?.response?.data || e.message);
      alert("Couldn’t follow back. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const acceptRequest = async (connectionId) => {
    if (busy[connectionId]) return;
    setBusy((b) => ({ ...b, [connectionId]: true }));
    try {
      await api.post(`/connections/requests/${connectionId}/accept`, {}, { headers });
      fetchRequests();
      fetchFollowers(); // you likely gained a follower
    } catch (e) {
      console.error("Accept failed", e?.response?.data || e.message);
      alert("Couldn’t accept. Try again.");
    } finally {
      setBusy((b) => ({ ...b, [connectionId]: false }));
    }
  };

  const declineRequest = async (connectionId) => {
    if (busy[connectionId]) return;
    setBusy((b) => ({ ...b, [connectionId]: true }));
    try {
      await api.delete(`/connections/requests/${connectionId}`, { headers });
      fetchRequests();
    } catch (e) {
      console.error("Decline failed", e?.response?.data || e.message);
      alert("Couldn’t decline. Try again.");
    } finally {
      setBusy((b) => ({ ...b, [connectionId]: false }));
    }
  };

  // Measure fixed header height for spacer
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setH = () => setHeaderH(el.offsetHeight || 0);
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    window.addEventListener("resize", setH);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setH);
    };
  }, []);

  return (
    <div className="min-h-screen bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 px-4 flex items-center">
          <BackButton className="text-blue-600" ariaLabel="Back" />
          <h1 className="text-[20px] text-blue-600 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
            Connetions
          </h1>
        </div>
      </header>

      {/* Top tabs */}
      <div className="mt-2 justify-center px-4 w-full flex items-center">
        <div className="grid grid-cols-3 w-[95%] gap-2">
          <TabButton active={tab === "followers"} onClick={() => setTab("followers")} label="Followers" />
          <TabButton active={tab === "following"} onClick={() => setTab("following")} label="Following" />
          <TabButton active={tab === "requests"} onClick={() => setTab("requests")} label="Requests" />
        </div>
      </div>

      {/* Content */}
      <main className="no-scrollbar w-full max-w-[390px] mx-auto pt-5 py-2 px-6">
        {/* Followers */}
        {tab === "followers" && (
          <UserList
            rows={followers}
            emptyText="No followers yet"
            rightRender={(u) => {
              const iFollow = amFollowing(u.id);

              if (!iFollow) {
                return (
                  <button
                    onClick={() => followBack(u.id)}
                    disabled={busy[u.id]}
                    className="!min-h-0 !min-w-0 px-3 py-1.5 rounded-full text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                  >
                    {busy[u.id] ? "…" : "Follow back"}
                  </button>
                );
              }

              return (
                <button
                  onClick={() => removeFollower(u.id)}
                  disabled={busy[u.id]}
                  className="!min-h-0 !min-w-0 text-sm px-3 py-1.5 rounded-full bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
                >
                  {busy[u.id] ? "…" : "Remove"}
                </button>
              );
            }}
          />
        )}

        {/* Following */}
        {tab === "following" && (
          <UserList
            rows={following}
            emptyText="You're not following anyone yet"
            rightRender={(u) => (
              <button
                onClick={() => removeFollowing(u.id)}
                disabled={busy[u.id]}
                className="!min-h-0 !min-w-0 px-3 py-1.5 text-sm rounded-full bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
              >
                {busy[u.id] ? "…" : "Remove"}
              </button>
            )}
          />
        )}

        {/* Requests */}
        {tab === "requests" && (
          <div className="space-y-8">
            {/* Received first */}
            <section>
              <h3 className="text-sm font-semibold text-blue-700 mb-2">Received ({recv.length})</h3>
              {recv.length === 0 ? (
                <p className="text-sm text-gray-500">No pending requests</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {recv.map((r) => {
                    const req = r.requester || r.sender || {};
                    const key = r.connection_id || r.id;
                    return (
                      <li key={key} className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Link to={`/profile/${req.id || ""}`}>
                              <img
                                src={toAvatar(req)}
                                alt={req.name || `User #${req.id}`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            </Link>
                            <div className="min-w-0">
                              <Link
                                to={`/profile/${req.id || ""}`}
                                className="font-medium hover:text-blue-600 truncate block"
                              >
                                {req.display_name ||
                                  req.name ||
                                  [req.first_name, req.last_name].filter(Boolean).join(" ") ||
                                  req.username ||
                                  `User ${req.id ?? ""}`}
                              </Link>
                              <p className="text-xs text-gray-500">sent you a request</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => acceptRequest(key)}
                              disabled={busy[key]}
                              className="!min-h !min-w-0 text-sm px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                            >
                              {busy[key] ? "…" : "Accept"}
                            </button>
                            <button
                              onClick={() => declineRequest(key)}
                              disabled={busy[key]}
                              className="!min-h !min-w-0 text-sm px-3 py-1.5 rounded-full bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
                            >
                              {busy[key] ? "…" : "Decline"}
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Sent */}
            <section>
              <h3 className="text-sm font-semibold text-blue-700 mb-2">Sent ({sent.length})</h3>
              {sent.length === 0 ? (
                <p className="text-sm text-gray-500">No sent requests</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {sent.map((r) => {
                    const to = r.addressee || r.receiver || {};
                    const status = r.status || "pending";
                    const key = r.connection_id || r.id;
                    return (
                      <li key={key} className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Link to={`/profile/${to.id || ""}`}>
                              <img
                                src={toAvatar(to)}
                                alt={to.name || `User #${to.id}`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            </Link>
                            <div className="min-w-0">
                              <Link
                                to={`/profile/${to.id || ""}`}
                                className="font-medium hover:text-blue-600 truncate block"
                              >
                                {to.display_name ||
                                  to.name ||
                                  [to.first_name, to.last_name].filter(Boolean).join(" ") ||
                                  to.username ||
                                  `User ${to.id ?? ""}`}
                              </Link>
                              <p className="text-xs text-gray-500">
                                {status === "accepted" ? "Accepted" : "Pending"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

/* -------------------- UI bits -------------------- */
function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`!min-h-0 !min-w-0 px-3 py-2 rounded-full text-sm font-medium transition ${
        active ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"
      }`}
    >
      {label}
    </button>
  );
}

function UserList({ rows, emptyText, rightRender }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((u) => (
        <li key={u.id} className="py-1">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Link to={`/profile/${u.id}`}>
                <img
                  src={toAvatar(u)}
                  alt={u.name || `User #${u.id}`}
                  className="w-10 h-10 rounded-full object-cover"
                />
              </Link>
              <Link to={`/profile/${u.id}`} className="font-medium hover:text-blue-600 truncate">
                {u.name || u.username || `User #${u.id}`}
              </Link>
            </div>
            {rightRender?.(u)}
          </div>
        </li>
      ))}
    </ul>
  );
}
